import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import { useCoreReady } from '../../store/data';
import { useBalanceSheet, useHealth, useThisMonth } from '../../store/selectors';
import { useCashflowModel } from '../plan/cashflowModel';
import { shortfallPlan } from '../../../finance';
import { useNetWorthHistory } from './netWorthHistory';
import { Alerts, DueList, ObligationsCard, BudgetCard, PriorityList } from './OverviewTasks';
import { Ring, TONE, fmtHealthValue } from '../../components/health';
import PageHeader from '../../components/kit/PageHeader';
import LeafBand from '../../components/kit/LeafBand';
import Money from '../../components/kit/Money';
import Pill from '../../components/kit/Pill';

const WEEKDAY = ['Chủ nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
const dm = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Tờ lịch hôm nay: ngày lớn, tiền dùng được, cần trả 30 ngày tới và điểm thấp nhất của số dư. */
function TodayLeaf() {
  const cf = useCashflowModel(30);
  const today = new Date();
  const low = cf.known.lowest;
  const short = cf.known.firstNegative;
  const gap = shortfallPlan(cf.known);
  return (
    <section aria-label="Hôm nay" className="leaf-curl overflow-hidden rounded-lg border border-border bg-card">
      <LeafBand left={`Tháng ${today.getMonth() + 1}`} right={String(today.getFullYear())} />
      <div className="grid gap-x-10 gap-y-6 p-5 sm:grid-cols-[auto_1fr] sm:p-8">
        <div className="flex items-end gap-4 sm:block">
          <p className="font-slab num text-[6.5rem] font-extrabold leading-[0.85] text-brand sm:text-[9rem]">{String(today.getDate()).padStart(2, '0')}</p>
          <p className="pb-1 text-xl font-semibold sm:mt-3">{WEEKDAY[today.getDay()]}</p>
        </div>
        <div className="grid content-center gap-5">
          <div>
            <p className="text-muted-foreground">Dùng được ngay</p>
            <Money value={cf.known.start} size="hero" />
            <p className="text-sm text-muted-foreground">Tiền trong ví thanh khoản, chưa trừ khoản nào sắp phải trả.</p>
          </div>
          <div>
            <p className="text-muted-foreground">Sắp phải trả trong 30 ngày</p>
            <Money value={cf.known.totalOut} size="figure" />
          </div>
          {short ? (
            <div role="alert" className="space-y-2 rounded-md bg-bad-soft px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="bad">Cần xử lý</Pill>
                <span className="text-sm text-bad">Tiền thanh khoản sẽ âm từ {dm(short)} nếu chỉ tính các khoản đã biết.</span>
              </div>
              {gap && (
                <div className="space-y-1.5 text-sm text-foreground">
                  <p>Thiếu nhiều nhất <b className="num">{formatVND(gap.gap)}</b> vào {dm(gap.lowestDate)}. Trong đó phải trả thẻ &amp; nợ <b className="num">{formatCompact(gap.fixedOut)}</b>, chi tiêu dự kiến <b className="num">{formatCompact(gap.flexibleOut)}</b>{gap.incomeIn > 0 ? <>, đã có thu dự kiến <b className="num">{formatCompact(gap.incomeIn)}</b></> : ' (chưa có khoản thu nào trước ngày đó)'}.</p>
                  {gap.gapAfterFlexible > 0
                    ? <p>Dù hoãn hết chi tiêu dự kiến vẫn còn thiếu <b className="num">{formatCompact(gap.gapAfterFlexible)}</b>: cần thêm nguồn thu, hoặc xem lại thời điểm các khoản trả thẻ &amp; nợ.</p>
                    : <p>Hoãn hoặc giảm chi tiêu dự kiến có thể lấp phần thiếu (cần giảm ít nhất <b className="num">{formatCompact(gap.gap)}</b>). Các khoản lớn nhất:</p>}
                  {gap.gapAfterFlexible <= 0 && (
                    <ul className="space-y-0.5">
                      {gap.flexible.map((e, i) => <li key={i} className="flex justify-between gap-3"><span className="min-w-0 truncate">{dm(e.date)} · {e.label}</span><span className="num shrink-0">{formatCompact(-e.amount)}</span></li>)}
                    </ul>
                  )}
                  <p className="text-muted-foreground">Chưa tính chi tiêu hằng ngày ngoài kế hoạch. <Link to="/v2/du-kien" className="font-medium text-primary hover:underline">Mở Dự kiến để dời hoặc bỏ qua khoản</Link></p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Đủ cho 30 ngày tới; thấp nhất {formatCompact(low.balance)} vào {dm(low.date)}.</p>
          )}
          <Link to="/v2/ke-hoach" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Xem dự báo dòng tiền <ArrowUpRight size={14} aria-hidden /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Overview() {
  const ready = useCoreReady();
  const sheet = useBalanceSheet();
  const health = useHealth();
  const month = useThisMonth();
  const trend = useNetWorthHistory();

  if (!ready) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-72" />
        <div className="grid gap-4 md:grid-cols-2"><Skeleton className="h-40" /><Skeleton className="h-40" /></div>
      </div>
    );
  }

  const net = month.income - month.expense;

  return (
    <div className="space-y-6">
      <PageHeader title="Tổng quan" question="Hôm nay thế nào?" />

      <TodayLeaf />
      <Alerts />
      <PriorityList />
      <DueList />

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Tháng {month.month}/{month.year} <span className="font-normal text-muted-foreground">đang diễn ra</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between"><span className="text-muted-foreground">Thu</span><Money value={month.income} size="row" tone="in" compact /></div>
            <div className="flex items-baseline justify-between"><span className="text-muted-foreground">Chi</span><Money value={month.expense} size="row" compact /></div>
            <div className="flex items-baseline justify-between border-t border-border pt-3"><span className="font-medium">Còn lại</span><Money value={net} size="figure" compact /></div>
            <p className="text-sm text-muted-foreground">Chi tính lúc quẹt, gồm chi bằng thẻ. Hoàn tiền được trừ đúng chiều.</p>
            <Link to="/v2/giao-dich" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Xem giao dịch tháng này <ArrowUpRight size={14} aria-hidden /></Link>
          </CardContent>
        </Card>
        <BudgetCard />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ObligationsCard />
        <Card className="min-w-0">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Tài sản ròng</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="font-slab num text-3xl font-bold">{formatVND(sheet.netWorth)}</p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
              <div><dt className="text-sm text-muted-foreground">Tài sản</dt><dd className="num font-medium">{formatCompact(sheet.totalAssets)}</dd><dd className="num text-sm text-muted-foreground">thanh khoản {formatCompact(sheet.liquid)}, dài hạn {formatCompact(sheet.longTerm)}</dd></div>
              <div><dt className="text-sm text-muted-foreground">Nợ</dt><dd className="num font-medium">{formatCompact(sheet.totalLiabilities)}</dd><dd className="num text-sm text-muted-foreground">thẻ {formatCompact(sheet.cardDebt)}, vay {formatCompact(sheet.loanDebt)}</dd></div>
            </dl>
            {trend && (
              <div className="space-y-1.5 border-t border-border pt-3">
                {trend.change !== null && trend.previous ? (
                  <p className="text-sm"><span className="text-muted-foreground">So với cuối tháng {trend.previous.month.slice(5)}/{trend.previous.month.slice(0, 4)}: </span><b className={cn('num', trend.change >= 0 ? 'text-good' : 'text-bad')}>{trend.change >= 0 ? '+' : '−'}{formatVND(Math.abs(trend.change))}</b></p>
                ) : <p className="text-sm text-muted-foreground">Đã bắt đầu ghi lại tài sản ròng mỗi tháng. Từ tháng sau sẽ thấy tăng hay giảm so với tháng trước.</p>}
                {trend.series.length >= 2 && (
                  <ul className="grid gap-0.5 text-sm" aria-label="Tài sản ròng theo tháng">
                    {trend.series.slice(-6).map(p => <li key={p.month} className="flex justify-between gap-3"><span className="text-muted-foreground">Tháng {p.month.slice(5)}/{p.month.slice(0, 4)}</span><span className="num">{formatCompact(p.netWorth)}</span></li>)}
                  </ul>
                )}
              </div>
            )}
            <Link to="/v2/tai-san" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">Xem Tài sản & Nợ <ArrowUpRight size={14} aria-hidden /></Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Sức khoẻ tài chính</CardTitle></CardHeader>
        <CardContent>
          {health.score.score === null ? (
            <p className="text-muted-foreground">Chưa đủ dữ liệu thu chi để chấm điểm. Ghi nhận vài tháng giao dịch, điểm sẽ xuất hiện ở đây.</p>
          ) : (
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4">
                <Ring score={health.score.score} status={health.score.status} />
                {health.score.status && <Pill tone={health.score.status === 'good' ? 'good' : health.score.status === 'warn' ? 'warn' : 'bad'}>{TONE[health.score.status].label}</Pill>}
              </div>
              <ul className="grid flex-1 gap-3">
                {health.score.components.map(c => (
                  <li key={c.key} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium">{c.label}</span>
                      <span className={cn('num', TONE[c.status].text)}>{fmtHealthValue(c.key, c.value)}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                      <div className={cn('h-full rounded-full transition-[width] duration-700 motion-reduce:transition-none', TONE[c.status].bar)} style={{ width: `${c.score}%` }} />
                    </div>
                    <p className="text-sm text-muted-foreground">Chuẩn: {c.target}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
